import React, { useState, useMemo, useEffect, useRef } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  Box,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  IconButton,
  styled,
  CircularProgress,
  Chip,
  Popover,
  Checkbox,
  FormControlLabel,
  Button,
  useTheme,
  useMediaQuery,
  Drawer,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  CalendarViewDay,
  CalendarViewWeek,
  FilterList,
} from '@mui/icons-material';
import Header from '../common/ToolsHeader';
import { useGetSlates } from '../common/queries/slateQueries';
import { SlateResponse } from '../common/store/types';
import { siteLogos, sportLogos } from '../utils/logos';
import CalendarView from './CalendarView';
import MultipleSlatesModal from './MultipleSlatesModal';

const queryClient = new QueryClient();

// Styled Components
const ScheduleContainer = styled(Box)(({ theme }) => ({
  height: '100vh',
  '@supports (height: 100dvh)': {
    height: '100dvh',
  },
  background: 'linear-gradient(180deg, #000A16 0%, #102341 100%)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  maxWidth: 1400,
  width: '100%',
  margin: '0 auto',
  padding: '24px 20px',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0, // Important for flex children to scroll properly
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    padding: '8px 0',
    maxWidth: '100%',
  },
}));

const CalendarHeader = styled(Stack)(({ theme }) => ({
  marginBottom: 24,
  flexShrink: 0,
  [theme.breakpoints.down('sm')]: {
    marginBottom: 8,
    padding: '0 8px',
  },
}));

const DateTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Oswald', sans-serif",
  fontWeight: 600,
  fontSize: 32,
  color: '#FFFFFF',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  [theme.breakpoints.down('sm')]: {
    fontSize: 18,
  },
}));

// Bottom sheet for mobile filters
const BottomSheetContent = styled(Box)({
  backgroundColor: '#0A1628',
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  padding: '24px 16px',
  maxHeight: '70vh',
  overflowY: 'auto',
});

const BottomSheetHandle = styled(Box)({
  width: 40,
  height: 4,
  backgroundColor: 'rgba(255, 255, 255, 0.3)',
  borderRadius: 2,
  margin: '0 auto 16px',
});

const BottomSheetTitle = styled(Typography)({
  fontFamily: "'Oswald', sans-serif",
  fontSize: 20,
  fontWeight: 600,
  color: '#FFFFFF',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: 16,
  textAlign: 'center',
});

const TodayButton = styled(Box)({
  padding: '6px 16px',
  background: 'linear-gradient(135deg, #015ACE 0%, #0142A0 100%)',
  borderRadius: 4,
  color: '#FFFFFF',
  fontFamily: "'Work Sans', sans-serif",
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, #0166E5 0%, #015ACE 100%)',
    transform: 'translateY(-1px)',
  },
});

const NavButton = styled(IconButton)({
  color: '#FFFFFF',
  backgroundColor: 'rgba(1, 90, 206, 0.2)',
  border: '1px solid rgba(1, 90, 206, 0.3)',
  width: 36,
  height: 36,
  '&:hover': {
    backgroundColor: 'rgba(1, 90, 206, 0.4)',
  },
});

const ViewToggle = styled(ToggleButtonGroup)({
  backgroundColor: 'rgba(0, 10, 22, 0.6)',
  border: '1px solid rgba(1, 90, 206, 0.3)',
  borderRadius: 8,
  '& .MuiToggleButton-root': {
    color: 'rgba(255, 255, 255, 0.6)',
    border: 'none',
    padding: '8px 16px',
    fontFamily: "'Work Sans', sans-serif",
    fontWeight: 500,
    fontSize: 13,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    '&.Mui-selected': {
      color: '#FFFFFF',
      backgroundColor: 'rgba(1, 90, 206, 0.4)',
      '&:hover': {
        backgroundColor: 'rgba(1, 90, 206, 0.5)',
      },
    },
    '&:hover': {
      backgroundColor: 'rgba(1, 90, 206, 0.2)',
    },
  },
});

const LoadingContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: 400,
});

const FilterButton = styled(Button)({
  backgroundColor: 'rgba(1, 90, 206, 0.2)',
  border: '1px solid rgba(1, 90, 206, 0.3)',
  borderRadius: 8,
  color: '#FFFFFF',
  fontFamily: "'Work Sans', sans-serif",
  fontWeight: 500,
  fontSize: 13,
  textTransform: 'none',
  padding: '6px 12px',
  '&:hover': {
    backgroundColor: 'rgba(1, 90, 206, 0.4)',
  },
});

const FilterChip = styled(Chip)({
  backgroundColor: 'rgba(1, 90, 206, 0.3)',
  color: '#FFFFFF',
  fontFamily: "'Work Sans', sans-serif",
  fontWeight: 500,
  fontSize: 12,
  height: 28,
  '& .MuiChip-deleteIcon': {
    color: 'rgba(255, 255, 255, 0.6)',
    '&:hover': {
      color: '#FFFFFF',
    },
  },
});

const FilterPopover = styled(Box)({
  backgroundColor: '#0A1628',
  border: '1px solid rgba(1, 90, 206, 0.3)',
  borderRadius: 8,
  padding: 16,
  minWidth: 200,
});

const FilterSection = styled(Box)({
  marginBottom: 16,
  '&:last-child': {
    marginBottom: 0,
  },
});

const FilterSectionTitle = styled(Typography)({
  fontFamily: "'Work Sans', sans-serif",
  fontSize: 11,
  fontWeight: 600,
  color: 'rgba(255, 255, 255, 0.5)',
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  marginBottom: 8,
});

const FilterCheckbox = styled(FormControlLabel)({
  marginLeft: 0,
  marginRight: 0,
  width: '100%',
  '& .MuiTypography-root': {
    fontFamily: "'Work Sans', sans-serif",
    fontSize: 13,
    color: '#FFFFFF',
  },
  '& .MuiCheckbox-root': {
    color: 'rgba(255, 255, 255, 0.4)',
    padding: 6,
    '&.Mui-checked': {
      color: '#015ACE',
    },
  },
});

const SelectAllButton = styled(Button)({
  fontSize: 11,
  textTransform: 'none',
  color: '#4DA3FF',
  padding: '2px 8px',
  minWidth: 'auto',
  '&:hover': {
    backgroundColor: 'rgba(1, 90, 206, 0.2)',
  },
});

type ViewMode = 'day' | 'week';

const ScheduleContent: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const history = useHistory();

  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [multipleSlatesModalOpen, setMultipleSlatesModalOpen] = useState(false);
  const [multipleSlates, setMultipleSlates] = useState<SlateResponse[]>([]);
  const [multipleSlatesHour, setMultipleSlatesHour] = useState<number>(0);

  // Filter state
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedSports, setSelectedSports] = useState<string[]>([]);
  const [selectedSites, setSelectedSites] = useState<string[]>([]);

  // Force day view on mobile
  useEffect(() => {
    if (isMobile && viewMode !== 'day') {
      setViewMode('day');
    }
  }, [isMobile, viewMode]);

  // Track if filters have been initialized (to prevent re-initializing after user clears)
  const sportsInitialized = useRef(false);
  const sitesInitialized = useRef(false);

  // Fetch all slates
  const { data: slateData, isLoading } = useGetSlates('DATAHUB', false);

  // Get unique sports and sites from data
  const availableSports = useMemo(() => {
    if (!slateData?.sports) return [];
    return slateData.sports.map(s => s.sport.toUpperCase());
  }, [slateData?.sports]);

  const availableSites = useMemo(() => {
    if (!slateData?.sites) return [];
    return slateData.sites.map(s => s.site.toUpperCase());
  }, [slateData?.sites]);

  // Initialize filters to all when data loads (only once)
  useEffect(() => {
    if (availableSports.length > 0 && !sportsInitialized.current) {
      setSelectedSports(availableSports);
      sportsInitialized.current = true;
    }
  }, [availableSports]);

  useEffect(() => {
    if (availableSites.length > 0 && !sitesInitialized.current) {
      setSelectedSites(availableSites);
      sitesInitialized.current = true;
    }
  }, [availableSites]);

  // Filter slates based on selected sports and sites
  // If no filters selected, show no slates (user clicked "None")
  const filteredSlates = useMemo(() => {
    if (!slateData?.slates) return [];
    // If either filter is empty, show no slates
    if (selectedSports.length === 0 || selectedSites.length === 0) return [];
    return slateData.slates.filter((slate) => {
      const sportMatch = selectedSports.includes(slate.sport.toUpperCase());
      const siteMatch = selectedSites.includes(slate.site.toUpperCase());
      return sportMatch && siteMatch;
    });
  }, [slateData?.slates, selectedSports, selectedSites]);

  const handleViewChange = (
    _event: React.MouseEvent<HTMLElement>,
    newView: ViewMode | null
  ) => {
    if (newView !== null) {
      setViewMode(newView);
    }
  };

  const handleDateNavigation = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (viewMode === 'day') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
    } else {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    }
    setCurrentDate(newDate);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  // Navigate to Data Hub with the slate selected
  const navigateToDataHub = (slate: SlateResponse) => {
    const sport = slate.sport?.toUpperCase() || 'NFL';
    const site = slate.site?.toLowerCase() || 'dk';
    history.push(`/datahub/${sport}?slate=${slate.slateId}&site=${site}`);
  };

  const handleSlateClick = (slate: SlateResponse) => {
    navigateToDataHub(slate);
  };

  const handleMultipleSlatesClick = (slates: SlateResponse[], hour: number) => {
    setMultipleSlates(slates);
    setMultipleSlatesHour(hour);
    setMultipleSlatesModalOpen(true);
  };

  const handleCloseMultipleSlatesModal = () => {
    setMultipleSlatesModalOpen(false);
    setMultipleSlates([]);
  };

  const handleSlateSelectFromMultiple = (slate: SlateResponse) => {
    navigateToDataHub(slate);
  };

  // Filter handlers
  const handleFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleSportToggle = (sport: string) => {
    setSelectedSports(prev =>
      prev.includes(sport)
        ? prev.filter(s => s !== sport)
        : [...prev, sport]
    );
  };

  const handleSiteToggle = (site: string) => {
    setSelectedSites(prev =>
      prev.includes(site)
        ? prev.filter(s => s !== site)
        : [...prev, site]
    );
  };

  const handleSelectAllSports = () => {
    setSelectedSports(availableSports);
  };

  const handleSelectAllSites = () => {
    setSelectedSites(availableSites);
  };

  const handleClearSports = () => {
    setSelectedSports([]);
  };

  const handleClearSites = () => {
    setSelectedSites([]);
  };

  const formatDateRange = () => {
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    };

    if (viewMode === 'day') {
      return currentDate.toLocaleDateString('en-US', options);
    }

    // Week view - show range
    const startOfWeek = new Date(currentDate);
    const dayOfWeek = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - dayOfWeek);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(endOfWeek.getDate() + 6);

    const startMonth = startOfWeek.toLocaleDateString('en-US', { month: 'short' });
    const endMonth = endOfWeek.toLocaleDateString('en-US', { month: 'short' });
    const startDay = startOfWeek.getDate();
    const endDay = endOfWeek.getDate();
    const year = endOfWeek.getFullYear();

    if (startMonth === endMonth) {
      return `${startMonth} ${startDay} - ${endDay}, ${year}`;
    }
    return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
  };

  const activeFilterCount =
    (selectedSports.length < availableSports.length ? 1 : 0) +
    (selectedSites.length < availableSites.length ? 1 : 0);

  return (
    <ScheduleContainer>
      <Helmet>
        <title>Slate Schedule | Stokastic Tools</title>
        <link
          href="https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600;700&family=Work+Sans:wght@400;500;600;700&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <Header />

      <ContentWrapper>
        <CalendarHeader
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          spacing={{ xs: 1, md: 2 }}
        >
          <Stack direction="row" alignItems="center" spacing={{ xs: 1, md: 2 }} flexWrap="wrap">
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <NavButton onClick={() => handleDateNavigation('prev')} sx={{ width: { xs: 32, md: 36 }, height: { xs: 32, md: 36 } }}>
                <ChevronLeft />
              </NavButton>
              <NavButton onClick={() => handleDateNavigation('next')} sx={{ width: { xs: 32, md: 36 }, height: { xs: 32, md: 36 } }}>
                <ChevronRight />
              </NavButton>
            </Stack>

            <TodayButton onClick={handleToday} sx={{ padding: { xs: '4px 10px', md: '6px 16px' }, fontSize: { xs: 11, md: 13 } }}>Today</TodayButton>

            <DateTitle>{formatDateRange()}</DateTitle>
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            flexWrap="wrap"
            sx={{
              alignSelf: { xs: 'flex-end', md: 'auto' },
              width: { xs: '100%', md: 'auto' },
              justifyContent: { xs: 'flex-end', md: 'flex-start' },
            }}
          >
            <Typography
              sx={{
                color: 'rgba(255, 255, 255, 0.6)',
                fontFamily: "'Work Sans', sans-serif",
                fontSize: 14,
                display: { xs: 'none', sm: 'block' },
              }}
            >
              {filteredSlates.length} slate{filteredSlates.length !== 1 ? 's' : ''}{' '}
              available
            </Typography>

            {/* Filter Button */}
            <FilterButton
              startIcon={<FilterList />}
              onClick={handleFilterClick}
              endIcon={activeFilterCount > 0 ? (
                <Chip
                  label={activeFilterCount}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: 10,
                    backgroundColor: '#FF6B35',
                    color: '#FFFFFF',
                  }}
                />
              ) : null}
            >
              Filters
            </FilterButton>

            {/* Desktop: Popover */}
            {!isMobile && (
              <Popover
                open={Boolean(filterAnchorEl)}
                anchorEl={filterAnchorEl}
                onClose={handleFilterClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                PaperProps={{
                  sx: {
                    backgroundColor: 'transparent',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                  },
                }}
              >
                <FilterPopover>
                  {/* Sports Filter */}
                  <FilterSection>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <FilterSectionTitle>Sports</FilterSectionTitle>
                      <Stack direction="row" spacing={0.5}>
                        <SelectAllButton onClick={handleSelectAllSports}>All</SelectAllButton>
                        <SelectAllButton onClick={handleClearSports}>None</SelectAllButton>
                      </Stack>
                    </Stack>
                    {availableSports.map((sport) => (
                      <FilterCheckbox
                        key={sport}
                        control={
                          <Checkbox
                            checked={selectedSports.includes(sport)}
                            onChange={() => handleSportToggle(sport)}
                            size="small"
                          />
                        }
                        label={
                          <Stack direction="row" alignItems="center" spacing={1}>
                            {sportLogos[sport]?.logo && (
                              <Box
                                sx={{
                                  width: 20,
                                  height: 20,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <img
                                  src={sportLogos[sport].logo}
                                  alt={sport}
                                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                />
                              </Box>
                            )}
                            <span>{sport}</span>
                          </Stack>
                        }
                      />
                    ))}
                  </FilterSection>

                  {/* Sites Filter */}
                  <FilterSection>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <FilterSectionTitle>Sites</FilterSectionTitle>
                      <Stack direction="row" spacing={0.5}>
                        <SelectAllButton onClick={handleSelectAllSites}>All</SelectAllButton>
                        <SelectAllButton onClick={handleClearSites}>None</SelectAllButton>
                      </Stack>
                    </Stack>
                    {availableSites.map((site) => (
                      <FilterCheckbox
                        key={site}
                        control={
                          <Checkbox
                            checked={selectedSites.includes(site)}
                            onChange={() => handleSiteToggle(site)}
                            size="small"
                          />
                        }
                        label={
                          <Stack direction="row" alignItems="center" spacing={1}>
                            {siteLogos[site.toLowerCase()]?.logo && (
                              <Box
                                sx={{
                                  width: 20,
                                  height: 20,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <img
                                  src={siteLogos[site.toLowerCase()].logo}
                                  alt={site}
                                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                />
                              </Box>
                            )}
                            <span>{siteLogos[site.toLowerCase()]?.name || site}</span>
                          </Stack>
                        }
                      />
                    ))}
                  </FilterSection>
                </FilterPopover>
              </Popover>
            )}

            {/* Mobile: Bottom Sheet */}
            <Drawer
              anchor="bottom"
              open={isMobile && Boolean(filterAnchorEl)}
              onClose={handleFilterClose}
              PaperProps={{
                sx: {
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                },
              }}
            >
              <BottomSheetContent>
                <BottomSheetHandle />
                <BottomSheetTitle>Filters</BottomSheetTitle>

                {/* Sports Filter */}
                <FilterSection>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <FilterSectionTitle>Sports</FilterSectionTitle>
                    <Stack direction="row" spacing={0.5}>
                      <SelectAllButton onClick={handleSelectAllSports}>All</SelectAllButton>
                      <SelectAllButton onClick={handleClearSports}>None</SelectAllButton>
                    </Stack>
                  </Stack>
                  {availableSports.map((sport) => (
                    <FilterCheckbox
                      key={sport}
                      control={
                        <Checkbox
                          checked={selectedSports.includes(sport)}
                          onChange={() => handleSportToggle(sport)}
                          size="small"
                        />
                      }
                      label={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          {sportLogos[sport]?.logo && (
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <img
                                src={sportLogos[sport].logo}
                                alt={sport}
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                              />
                            </Box>
                          )}
                          <span>{sport}</span>
                        </Stack>
                      }
                    />
                  ))}
                </FilterSection>

                {/* Sites Filter */}
                <FilterSection>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <FilterSectionTitle>Sites</FilterSectionTitle>
                    <Stack direction="row" spacing={0.5}>
                      <SelectAllButton onClick={handleSelectAllSites}>All</SelectAllButton>
                      <SelectAllButton onClick={handleClearSites}>None</SelectAllButton>
                    </Stack>
                  </Stack>
                  {availableSites.map((site) => (
                    <FilterCheckbox
                      key={site}
                      control={
                        <Checkbox
                          checked={selectedSites.includes(site)}
                          onChange={() => handleSiteToggle(site)}
                          size="small"
                        />
                      }
                      label={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          {siteLogos[site.toLowerCase()]?.logo && (
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <img
                                src={siteLogos[site.toLowerCase()].logo}
                                alt={site}
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                              />
                            </Box>
                          )}
                          <span>{siteLogos[site.toLowerCase()]?.name || site}</span>
                        </Stack>
                      }
                    />
                  ))}
                </FilterSection>
              </BottomSheetContent>
            </Drawer>

            {/* Hide week toggle on mobile - only show day view */}
            {!isMobile && (
              <ViewToggle
                value={viewMode}
                exclusive
                onChange={handleViewChange}
                aria-label="calendar view mode"
              >
                <ToggleButton value="day" aria-label="day view">
                  <CalendarViewDay sx={{ mr: 0.5, fontSize: 18 }} />
                  Day
                </ToggleButton>
                <ToggleButton value="week" aria-label="week view">
                  <CalendarViewWeek sx={{ mr: 0.5, fontSize: 18 }} />
                  Week
                </ToggleButton>
              </ViewToggle>
            )}
          </Stack>
        </CalendarHeader>

        {/* Active Filters Display */}
        {(selectedSports.length < availableSports.length || selectedSites.length < availableSites.length) && (
          <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" sx={{ gap: 1, px: { xs: 1, sm: 0 } }}>
            {selectedSports.length < availableSports.length && selectedSports.length > 0 && (
              <>
                {selectedSports.map((sport) => (
                  <FilterChip
                    key={sport}
                    label={sport}
                    onDelete={() => handleSportToggle(sport)}
                    size="small"
                  />
                ))}
              </>
            )}
            {selectedSites.length < availableSites.length && selectedSites.length > 0 && (
              <>
                {selectedSites.map((site) => (
                  <FilterChip
                    key={site}
                    label={site}
                    onDelete={() => handleSiteToggle(site)}
                    size="small"
                    sx={{ backgroundColor: 'rgba(0, 179, 126, 0.3)' }}
                  />
                ))}
              </>
            )}
          </Stack>
        )}

        {isLoading ? (
          <LoadingContainer>
            <CircularProgress sx={{ color: '#015ACE' }} />
          </LoadingContainer>
        ) : (
          <CalendarView
            viewMode={viewMode}
            currentDate={currentDate}
            slates={filteredSlates}
            onSlateClick={handleSlateClick}
            onMultipleSlatesClick={handleMultipleSlatesClick}
          />
        )}
      </ContentWrapper>

      <MultipleSlatesModal
        open={multipleSlatesModalOpen}
        onClose={handleCloseMultipleSlatesModal}
        slates={multipleSlates}
        hour={multipleSlatesHour}
        onSlateSelect={handleSlateSelectFromMultiple}
      />
    </ScheduleContainer>
  );
};

const Schedule: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ScheduleContent />
    </QueryClientProvider>
  );
};

export default Schedule;
